import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, User, Award, Save, Lock, Share2, Mail, ShieldCheck, GraduationCap, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';

/* Profile is scoped to identity, account, and achievements. */

const TIER_LABELS = { school: 'School', intermediate: 'Intermediate', btech: 'B.Tech' };

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="glass-card" style={{ padding: '2rem' }}>
      <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--navy-dark)', letterSpacing: '-0.01em' }}>
        <Icon size={17} color="#64748b" strokeWidth={2} />{title}
      </h3>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
      <Icon size={15} color="#94a3b8" style={{ flexShrink: 0 }} />
      <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600, width: '7rem', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--navy-dark)', fontSize: '0.85rem', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

export default function Profile() {
  const { user, profile, updateProfile, loading: authLoading } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: '', bio: '', institution: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) { navigate('/'); return; }
    if (profile) setForm({ full_name: profile.full_name || '', bio: profile.bio || '', institution: profile.institution || '' });
  }, [profile, user, authLoading, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ full_name: form.full_name, bio: form.bio, institution: form.institution });
      showAlert('Profile Updated', 'Your profile has been saved successfully.');
    } catch {
      showAlert('Error', 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) return <div style={{ padding: '6rem 2rem', textAlign: 'center', color: '#64748b' }}>Loading profile...</div>;
  if (!user) return null;

  const avatarSrc = user.avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`;
  const roleLabel = profile?.role === 'mentor' ? 'Mentor' : profile?.role === 'admin' ? 'Administrator' : (TIER_LABELS[profile?.studentTier] || 'Student');
  const memberSince = profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '—';

  return (
    <div className="profile-grid" style={{ maxWidth: '1000px', margin: '0 auto' }}>

      {/* ── Identity sidebar ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', position: 'sticky', top: '1.5rem' }}>
        <div className="glass-card" style={{ padding: '2rem 1.75rem', textAlign: 'center' }}>
          <div style={{ position: 'relative', width: '6rem', margin: '0 auto 1.25rem' }}>
            <div style={{ width: '6rem', height: '6rem', borderRadius: '50%', border: '1px solid rgba(0,0,0,0.08)', padding: '3px' }}>
              <img src={avatarSrc} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            </div>
            <button
              style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '2rem', height: '2rem', background: 'var(--navy-dark)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid #fff' }}
              title="Change photo"
            ><Camera size={13} /></button>
          </div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--navy-dark)', marginBottom: '0.3rem', letterSpacing: '-0.01em' }}>{profile?.full_name || 'Scholar'}</h1>
          <span style={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: 700, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '999px', padding: '0.25rem 0.75rem', marginBottom: '1.1rem' }}>
            {roleLabel}
          </span>
          <p style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
            {profile?.bio || 'No bio added yet.'}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '1.25rem' }}>
            <div style={{ padding: '0.7rem', background: '#f8fafc', borderRadius: '0.6rem', border: '1px solid rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--navy-dark)' }}>{profile?.score || 0}</div>
              <div style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Score</div>
            </div>
            <div style={{ padding: '0.7rem', background: '#f8fafc', borderRadius: '0.6rem', border: '1px solid rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--navy-dark)' }}>{profile?.streak || 0}</div>
              <div style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Day Streak</div>
            </div>
          </div>

          <button
            onClick={() => { navigator.clipboard?.writeText(window.location.origin + '/portfolio/' + user.id); showAlert('Link Copied', 'Your portfolio link has been copied to clipboard.'); }}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.7rem', background: '#ffffff', border: '1px solid #e2e8f0', color: '#334155', borderRadius: '0.6rem', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--navy-dark)'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
          >
            <Share2 size={14} />Share Portfolio
          </button>
        </div>

        <div style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
          <Calendar size={12} />Member since {memberSince}
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        <SectionCard icon={User} title="Personal Information">
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {[
              { label: 'Full Name', key: 'full_name', type: 'text', placeholder: 'Your full name' },
              { label: 'Institution', key: 'institution', type: 'text', placeholder: 'Your school or university' },
              { label: 'Bio', key: 'bio', type: 'textarea', placeholder: 'A short line about yourself' },
            ].map(({ label, key, type, placeholder }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', fontWeight: 700, letterSpacing: '0.04em', marginBottom: '0.4rem' }}>{label}</label>
                {type === 'textarea' ? (
                  <textarea
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    rows={3}
                    style={{ width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', color: 'var(--navy-dark)', borderRadius: '0.6rem', padding: '0.7rem 0.9rem', fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', resize: 'vertical', transition: 'border-color 0.2s' }}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--navy-dark)'}
                    onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                  />
                ) : (
                  <input
                    type={type}
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    style={{ width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', color: 'var(--navy-dark)', borderRadius: '0.6rem', padding: '0.7rem 0.9rem', fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.2s' }}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--navy-dark)'}
                    onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                  />
                )}
              </div>
            ))}
            <button type="submit" disabled={saving} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.8rem', background: 'var(--navy-dark)', color: 'white', borderRadius: '0.6rem', fontWeight: 700, fontSize: '0.85rem', cursor: saving ? 'not-allowed' : 'pointer', border: 'none', fontFamily: 'inherit', opacity: saving ? 0.7 : 1, marginTop: '0.25rem' }}>
              <Save size={15} />{saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </SectionCard>

        <SectionCard icon={Lock} title="Account & Security">
          <InfoRow icon={Mail} label="Email" value={user.email} />
          <InfoRow icon={ShieldCheck} label="Auth Method" value="Email & Password" />
          {profile?.studentTier && <InfoRow icon={GraduationCap} label="Class Tier" value={TIER_LABELS[profile.studentTier]} />}
        </SectionCard>

        <SectionCard icon={Award} title="Badges">
          {profile?.badges?.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '0.75rem' }}>
              {profile.badges.map((badge, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.9rem', background: '#f8fafc', borderRadius: '0.6rem', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <div style={{ width: '2.5rem', height: '2.5rem', background: '#eef2ff', borderRadius: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.4rem' }}>
                    <Award size={17} color="#6366f1" />
                  </div>
                  <span style={{ color: 'var(--navy-dark)', fontSize: '0.68rem', fontWeight: 700, textAlign: 'center' }}>{badge}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '1.75rem', textAlign: 'center', border: '1px dashed #e2e8f0', borderRadius: '0.6rem' }}>
              <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>No badges earned yet.</p>
            </div>
          )}
        </SectionCard>

      </div>
    </div>
  );
}
